import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
	Box,
	Grid,
	Link,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import NumberFormat from 'react-number-format';
import {
	CerebroCheckbox,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroIconButton,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';
import {
	DealRequestAllocation,
	MatchingLenderStat,
	MatchingParams,
	SelectedTranche,
	StructureTranches,
	Tier1BankTypes,
	TrancheEvaluation,
} from '../../shared/models';
import { EnumUtils, FormikUtils, MatchingUtils, NumberUtils, HtmlUtils, DateUtils } from '../../shared/utils';
import React from 'react';
import { useSnackbar } from 'notistack';
import { useMatchingTrancheLendersQuery } from '../../shared/hooks/Queries/useMatchingTrancheLendersQuery';
import { PlatformRootUrl } from '../../shared/utils/platform';
import { Formik } from 'formik';
import { useMutation } from 'react-query';
import { Urls, useApi } from '../../shared/context';
import { CerebroLink } from '../../shared/components/CerebroLink';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useMatchingTermSheetFacilitiesQuery } from '../../shared/hooks';
import {
	GetFacilityTypeDescription,
	GetStructureTrancheDescription,
	GetTier1BankTypeDescription,
} from '../../shared/utils/enum';
import { getApiResponseData } from '../../shared/utils/axios';
import { LenderMatchRowClickResponse, LenderMatchTableRow } from './LenderMatchTableRow';
import { UnavailableReasonDrawer } from '..';

type MatchingGridType = 'available' | 'unavailable' | 'preferred' | 'selected';

interface MatchingGridProps {
	dealGuid?: string;
	allocation?: DealRequestAllocation;
	tranches?: TrancheEvaluation[];
	stats?: MatchingLenderStat;
	params?: MatchingParams;
	tab: MatchingGridType;
	isFetching: boolean;
}

export const MatchingGrid: React.FC<MatchingGridProps> = ({
	dealGuid,
	allocation,
	tranches,
	stats,
	params,
	tab,
	isFetching,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const navigate = useNavigate();
	const { guid } = useParams<'guid'>();
	const dealGuidSafe = dealGuid ?? '';
	const [trancheSelected, setTrancheSelected] = React.useState<SelectedTranche | null>(null);
	const [trancheDetail, setTrancheDetail] = React.useState<TrancheEvaluation | null>(null);
	const [unavailableReason, setUnavailableReason] = React.useState<TrancheEvaluation | null>(null);
	const [addToDataRoom, setAddToDataRoom] = React.useState<boolean>(false);
	const [termSheetSelected, setTermSheetSelected] = React.useState<TrancheEvaluation | null>(null);
	const [drillInFilter, setDrillInFilter] = React.useState<number>(0);
	const [searchMode, setSearchMode] = React.useState<number>(1);

	const onLoadLenders = (mode: number, tier1: number, finderFee: boolean, structure?: number | null) => {
		setTrancheSelected({ mode, tier1, finderFee, structure });
	};

	const lendersQuery = useMatchingTrancheLendersQuery(
		dealGuid,
		trancheSelected?.mode,
		trancheSelected?.tier1,
		trancheSelected?.structure ?? null,
		trancheSelected?.finderFee,
		drillInFilter,
		searchMode,
		{ enabled: trancheSelected != null, refetchOnWindowFocus: false }
	);

	const termSheetQuery = useMatchingTermSheetFacilitiesQuery(
		dealGuid,
		termSheetSelected?.structureTranche,
		termSheetSelected?.tier1BankType,
		{ enabled: termSheetSelected != null, refetchOnWindowFocus: false }
	);

	const lendersMutation = useMutation(
		async ({ values }: LendersValuesMutationVariables) => {
			const { data, error } = await getApiResponseData<void>(
				addToDataRoom
					? api.post(
							CerebroApiEndpoints.ParticipantList.Root(dealGuid, trancheSelected?.structure),
							values.bankDealProfileGuid
					  )
					: api.post(
							CerebroApiEndpoints.LenderList.Root(dealGuid, trancheSelected?.structure),
							values.bankDealProfileGuid
					  )
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				const successMessage = addToDataRoom
					? 'Successfully added selected lenders to the data room participant list'
					: 'Successfully added selected lenders to the teaser list';
				snackbar.enqueueSnackbar(successMessage, {
					variant: 'success',
				});

				setTrancheSelected(null);
				setAddToDataRoom(false);
				navigate(`/${guid}/${Urls.Teasers.Root}`);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	const drawerRef = React.useRef<HTMLDivElement>(null);
	const drawer2Ref = React.useRef<HTMLDivElement>(null);

	return (
		<>
			{isFetching && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}

			{!isFetching && (allocation == null || tranches == null || tranches.length == 0 || dealGuid == null) && (
				<CerebroStyledBox>
					<CerebroText sx={{ marginBottom: 2 }}>
						There are no tranches that currently match for this deal using this filter. Please check and
						update the details of the deal.
					</CerebroText>
				</CerebroStyledBox>
			)}

			{!isFetching && allocation != null && tranches != null && tranches.length > 0 && dealGuid != null && (
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Tranche</TableCell>
								<TableCell>Tier 1</TableCell>
								<TableCell>Lender Count</TableCell>
								<TableCell>Term Sheets</TableCell>
								<TableCell>Facility</TableCell>
								<TableCell>Collateral</TableCell>
								<TableCell>Metric</TableCell>
								<TableCell>Multiplier</TableCell>
								<TableCell>Capacity</TableCell>
								<TableCell>Base</TableCell>
								<TableCell>Base Rate</TableCell>
								<TableCell>Rate</TableCell>
								<TableCell>Amortization</TableCell>
								<TableCell>Debt Service Coverage</TableCell>
								<TableCell>Required DSCR</TableCell>
								<TableCell>Required EBITDA</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{tranches.map((item: TrancheEvaluation) => {
								const dscr = item.dscr ?? 0;
								const stat =
									item.tier1BankType == 'Commercial'
										? stats?.dealSizeBankMatches
										: stats?.dealSizeNonBankMatches;
								const finderFeeStat =
									item.tier1BankType == 'Commercial'
										? stats?.dealSizeBankFinderFeeMatches
										: stats?.dealSizeNonBankFinderFeeMatches;
								const rowKey = `${item.structureTranche?.toString() ?? ''}-${
									item.tier1BankType?.toString() ?? ''
								}-${item.profileTangibleAssetType?.toString() ?? ''}-${
									item.profileRealEstateType?.toString() ?? ''
								}-${tab}`;
								const noPricing =
									item.tier1BankType == 'NonBank' &&
									(item.structureTranche == 'AssetBasedWorkingCapitalAccountsReceivable' ||
										item.structureTranche == 'AssetBasedWorkingCapitalInventory' ||
										item.structureTranche == 'Factoring' ||
										item.structureTranche == 'BridgeLoan');
								const showDetailIcon =
									(item.overrideCapacity != null && item.overrideCapacity > 0) ||
									item.isLimitedByCollateral;
								const trancheName = getFullTrancheDescription(item);

								return (
									<TableRow key={rowKey}>
										<TableCell>
											{tab === 'unavailable' ? (
												<CerebroLink
													title={'Click to see reasons the tranche is unavailable'}
													onClick={() => setUnavailableReason(item)}
													sx={{ cursor: 'pointer' }}
												>
													{trancheName}
												</CerebroLink>
											) : (
												<span>{trancheName}</span>
											)}
										</TableCell>
										<TableCell>
											{EnumUtils.GetTier1BankTypeDescription(item.tier1BankType)}
										</TableCell>
										<TableCell>
											<LenderCountLink
												stat={stat}
												numMatches={item.numberMatches}
												tier1BankType={item.tier1BankType}
												structureTranche={item.structureTranche}
												loadLenders={onLoadLenders}
											/>
											<span> / </span>
											<LenderCountLink
												stat={stat}
												finderFees
												numMatches={item.numberMatchesFinderFee}
												tier1BankType={item.tier1BankType}
												structureTranche={item.structureTranche}
												loadLenders={onLoadLenders}
											/>
										</TableCell>
										<TableCell>
											<CerebroLink
												title="View matching term sheet facilities"
												onClick={() => setTermSheetSelected(item)}
												sx={{ cursor: 'pointer' }}
											>
												{item.termSheetMatches}
											</CerebroLink>
										</TableCell>
										<TableCell>
											{EnumUtils.GetFacilityTypeDescription(item.facilityType1)}
										</TableCell>
										<TableCell>
											{EnumUtils.GetCollateralSecurityTypeDescription(
												item.collateralSecurityType
											)}
										</TableCell>
										<TableCell>
											<NumberFormat
												value={item.metric}
												displayType="text"
												thousandSeparator={true}
												prefix="$"
											/>
										</TableCell>
										<TableCell>{item.multiplier}</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex' }}>
												{item.overrideCapacity != null ? (
													<NumberFormat
														value={Math.round(item.overrideCapacity)}
														displayType="text"
														thousandSeparator={true}
														prefix="$"
													/>
												) : (
													<NumberFormat
														value={Math.round(item.metricCapacity)}
														displayType="text"
														thousandSeparator={true}
														prefix="$"
													/>
												)}
												{showDetailIcon && (
													<CerebroIconButton
														onClick={() => setTrancheDetail(item)}
														sx={theme => ({ color: theme.palette.info.main })}
														title="Click to see details"
													>
														<FontAwesomeIcon icon={faInfoCircle} size="xs" />
													</CerebroIconButton>
												)}
											</Box>
										</TableCell>
										<TableCell>
											{EnumUtils.GetTypicalCreditSpreadTypeDescription(item.baseRateType)}
										</TableCell>
										<TableCell>{item.baseRate}</TableCell>
										<TableCell>{item.rate}</TableCell>
										<TableCell>{!noPricing && item.amortization}</TableCell>
										<TableCell>
											{!noPricing && (
												<NumberFormat
													value={Math.round(dscr)}
													displayType="text"
													thousandSeparator={true}
													prefix="$"
												/>
											)}
										</TableCell>
										<TableCell>{!noPricing && item.minimumDscr}</TableCell>
										<TableCell>
											{!noPricing && (
												<NumberFormat
													value={Math.round(item.requiredEbitda ?? 0)}
													displayType="text"
													thousandSeparator={true}
													prefix="$"
												/>
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<UnavailableReasonDrawer
				dealGuid={dealGuidSafe}
				tranche={unavailableReason}
				onClose={() => setUnavailableReason(null)}
			/>

			<CerebroDrawer open={trancheDetail != null} onClose={() => setTrancheDetail(null)}>
				<CerebroHeading>
					{EnumUtils.GetStructureTrancheDescription(trancheDetail?.structureTranche)}
				</CerebroHeading>
				<CerebroHeading size="h3">
					{EnumUtils.GetTier1BankTypeDescription(trancheDetail?.tier1BankType)}
				</CerebroHeading>

				{trancheDetail?.overrideCapacity && (
					<>
						{trancheDetail?.isLimitedByCollateral ? (
							<CerebroText sx={{ marginTop: 2 }}>
								<strong>{NumberUtils.FormatCurrency(trancheDetail?.metricCapacity)}</strong> based on
								available collateral and{' '}
								<strong>{NumberUtils.FormatCurrency(trancheDetail?.overrideCapacity)}</strong> based on
								available cash flow.
							</CerebroText>
						) : (
							<CerebroText sx={{ marginTop: 2 }}>
								<strong>{NumberUtils.FormatCurrency(trancheDetail?.overrideCapacity)}</strong>{' '}
								overridden from{' '}
								<strong>{NumberUtils.FormatCurrency(trancheDetail?.metricCapacity)}</strong> based on
								available cash flow.
							</CerebroText>
						)}
					</>
				)}

				{trancheDetail?.isLimitedByCollateral && (
					<>
						{trancheDetail?.overrideCapacity == null && (
							<CerebroText sx={{ marginTop: 2 }}>
								Capacity limited by available assets. A strong personal guarantee may increase available
								capacity.
							</CerebroText>
						)}

						<CerebroText sx={{ marginTop: 2 }}>
							<strong>{NumberUtils.FormatCurrency(params?.totalCollateral)}</strong> of assets support
							this tranche:
						</CerebroText>

						<ul>
							{((params?.balanceSheetEquipment != null && params.balanceSheetEquipment > 0) ||
								(params?.totalEquipmentPurchaseAmount != null &&
									params.totalEquipmentPurchaseAmount > 0)) && (
								<li>
									Equipment:{' '}
									<strong>
										{NumberUtils.FormatCurrency(
											((params.totalEquipmentPurchaseAmount ?? 0) +
												(params?.balanceSheetEquipment ?? 0)) *
												0.8,
											'currency'
										)}
									</strong>{' '}
									advanced on{' '}
									<strong>
										{NumberUtils.FormatCurrency(
											(params.totalEquipmentPurchaseAmount ?? 0) +
												(params?.balanceSheetEquipment ?? 0),
											'currency'
										)}
									</strong>
								</li>
							)}

							{params?.balanceSheetAr != null && params.balanceSheetAr > 0 && (
								<li>
									A/R:{' '}
									<strong>{NumberUtils.FormatCurrency((params?.balanceSheetAr ?? 0) * 0.85)}</strong>{' '}
									advanced on <strong>{NumberUtils.FormatCurrency(params?.balanceSheetAr)}</strong>
								</li>
							)}

							{params?.balanceSheetInventory != null && params.balanceSheetInventory > 0 && (
								<li>
									Inventory:{' '}
									<strong>
										{NumberUtils.FormatCurrency(
											(params?.balanceSheetInventory ?? 0) * 0.4,
											'currency'
										)}
									</strong>{' '}
									advanced on{' '}
									<strong>{NumberUtils.FormatCurrency(params?.balanceSheetInventory)}</strong>
								</li>
							)}

							{params?.balanceSheetCash != null && params.balanceSheetCash > 0 && (
								<li>
									Cash/Marketables:{' '}
									<strong>
										{NumberUtils.FormatCurrency((params?.balanceSheetCash ?? 0) * 0.95)}
									</strong>{' '}
									advanced on <strong>{NumberUtils.FormatCurrency(params?.balanceSheetCash)}</strong>
								</li>
							)}

							{((params?.balanceSheetRealEstate != null && params.balanceSheetRealEstate > 0) ||
								(params?.totalRealEstateAmount != null && params.totalRealEstateAmount > 0)) && (
								<li>
									Real Estate:{' '}
									<strong>
										{NumberUtils.FormatCurrency(
											((params.totalRealEstateAmount ?? 0) +
												(params?.balanceSheetRealEstate ?? 0)) *
												0.8,
											'currency'
										)}
									</strong>{' '}
									advanced on{' '}
									<strong>
										{NumberUtils.FormatCurrency(
											(params.totalRealEstateAmount ?? 0) + (params?.balanceSheetRealEstate ?? 0),
											'currency'
										)}
									</strong>
								</li>
							)}

							{params?.balanceSheetFurnitureFixtures != null && params.balanceSheetFurnitureFixtures > 0 && (
								<li>
									Furnitures/Fixtures:{' '}
									<strong>
										{NumberUtils.FormatCurrency(
											(params?.balanceSheetFurnitureFixtures ?? 0) * 0.4,
											'currency'
										)}
									</strong>{' '}
									advanced on{' '}
									<strong>{NumberUtils.FormatCurrency(params?.balanceSheetFurnitureFixtures)}</strong>
								</li>
							)}
						</ul>
					</>
				)}
			</CerebroDrawer>

			{lendersQuery.isSuccess && (
				<CerebroDrawer ref={drawerRef} open={trancheSelected != null} onClose={() => setTrancheSelected(null)}>
					<Formik
						onSubmit={FormikUtils.MutateOnSubmit(lendersMutation.mutate)}
						initialValues={{
							bankDealProfileGuid: [] as string[],
						}}
					>
						{({ isSubmitting, handleSubmit, values, setFieldValue }) => (
							<CerebroForm
								flatIndicator
								onSubmit={handleSubmit}
								scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
							>
								<Grid container spacing={2} sx={{ marginBottom: 3 }}>
									{(trancheSelected?.structure == 1 || trancheSelected?.structure == 2) && (
										<Grid item xs={12} sm={6} md={4}>
											<CerebroDropdown
												value={drillInFilter}
												onChange={data => setDrillInFilter(data.target.value as number)}
											>
												<CerebroDropdownMenuItem value={-1}>
													All Lenders
												</CerebroDropdownMenuItem>
												<CerebroDropdownMenuItem value={0}>Full Amount</CerebroDropdownMenuItem>
												<CerebroDropdownMenuItem value={1}>
													Partial Amount
												</CerebroDropdownMenuItem>
											</CerebroDropdown>
										</Grid>
									)}
									<Grid item xs={12} sm={6} md={4}>
										<CerebroDropdown
											value={searchMode}
											onChange={data => setSearchMode(data.target.value as number)}
										>
											<CerebroDropdownMenuItem value="1">Include All</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem value="2">Exclude Active</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem value="3">
												Exclude All Invited
											</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem value="4">
												Specialty Industry
											</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem value="5">
												Loan Watch Subscribers
											</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem value="6">New Lenders</CerebroDropdownMenuItem>
										</CerebroDropdown>
									</Grid>
								</Grid>

								<TableContainer>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>Select</TableCell>
												<TableCell>Lender Name</TableCell>
												<TableCell>Bank Name</TableCell>
												<TableCell>Lending Capacity</TableCell>
												<TableCell>Tier 3</TableCell>
												<TableCell>Criteria</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{(lendersQuery.isLoading || lendersQuery.isFetching) && (
												<>
													<TableRow>
														<TableCell colSpan={6}>
															<Skeleton />
														</TableCell>
													</TableRow>
													<TableRow>
														<TableCell colSpan={6}>
															<Skeleton />
														</TableCell>
													</TableRow>
													<TableRow>
														<TableCell colSpan={6}>
															<Skeleton />
														</TableCell>
													</TableRow>
												</>
											)}

											{lendersQuery.isSuccess &&
												lendersQuery.data.map(lender => {
													return (
														<LenderMatchTableRow
															key={lender.bankDealProfileGuid}
															lender={lender}
															isSubmitting={false}
															showCapacityColumn={true}
															allowSelection={true}
															isChecked={
																values.bankDealProfileGuid.find(
																	w => w == lender.bankDealProfileGuid
																) != null
															}
															onChange={(item: LenderMatchRowClickResponse) => {
																if (item.isChecked) {
																	values.bankDealProfileGuid.push(
																		item.bankDealProfileGuid
																	);
																	setFieldValue(
																		'bankDealProfileGuid',
																		values.bankDealProfileGuid
																	);
																} else {
																	const id = values.bankDealProfileGuid.indexOf(
																		item.bankDealProfileGuid
																	);
																	if (id > -1) {
																		values.bankDealProfileGuid.splice(id, 1);
																		setFieldValue(
																			'bankDealProfileGuid',
																			values.bankDealProfileGuid
																		);
																	}
																}
															}}
														/>
													);
												})}
										</TableBody>
									</Table>
								</TableContainer>

								<CerebroPrimaryButton
									type="submit"
									loading={isSubmitting}
									disabled={!values.bankDealProfileGuid || values.bankDealProfileGuid.length == 0}
									sx={{ marginTop: 6 }}
									onClick={() => setAddToDataRoom(false)}
								>
									Add to Teaser List
								</CerebroPrimaryButton>

								<CerebroSecondaryButton
									type="submit"
									loading={isSubmitting}
									disabled={!values.bankDealProfileGuid || values.bankDealProfileGuid.length == 0}
									sx={{ marginTop: 6, marginLeft: 1 }}
									onClick={() => setAddToDataRoom(true)}
								>
									Add to Data Room
								</CerebroSecondaryButton>
							</CerebroForm>
						)}
					</Formik>
				</CerebroDrawer>
			)}

			{termSheetQuery.isSuccess && (
				<CerebroDrawer
					ref={drawer2Ref}
					open={termSheetSelected != null}
					onClose={() => setTermSheetSelected(null)}
				>
					<CerebroHeading>
						{GetTier1BankTypeDescription(termSheetSelected?.tier1BankType)}{' '}
						{GetStructureTrancheDescription(termSheetSelected?.structureTranche)}
					</CerebroHeading>
					<CerebroText sx={{ marginBottom: 3, marginTop: 1 }}>
						The following term sheet facilities match to some of the elements of this deal.
					</CerebroText>

					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Company</TableCell>
									<TableCell>Lender</TableCell>
									<TableCell>Date</TableCell>
									<TableCell>Facility Type</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Matched Elements</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{termSheetQuery.isLoading && (
									<>
										<TableRow>
											<TableCell colSpan={8}>
												<Skeleton />
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell colSpan={8}>
												<Skeleton />
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell colSpan={8}>
												<Skeleton />
											</TableCell>
										</TableRow>
									</>
								)}

								{termSheetQuery.isSuccess &&
									termSheetQuery.data.map(sheet => {
										return (
											<TableRow key={sheet.termSheetFacilityGuid}>
												<TableCell>{sheet.companyName}</TableCell>
												<TableCell>{sheet.bankName}</TableCell>
												<TableCell>
													{DateUtils.FormatStringAsShortDate(sheet.dateReceived.toString())}
												</TableCell>
												<TableCell>{GetFacilityTypeDescription(sheet.facilityType)}</TableCell>
												<TableCell>
													{NumberUtils.FormatCurrency(sheet.facilityAmount)}
												</TableCell>
												<TableCell>
													{sheet.industryMatch ? (
														<CerebroText variant="small">Industry</CerebroText>
													) : (
														<></>
													)}
													{sheet.geographyMatch ? (
														<CerebroText variant="small">Geography</CerebroText>
													) : (
														<></>
													)}
													{sheet.borrowerMatch ? (
														<CerebroText variant="small">{sheet.borrowerLabel}</CerebroText>
													) : (
														<></>
													)}
													{sheet.dealSizeMatch ? (
														<CerebroText variant="small">Deal Size</CerebroText>
													) : (
														<></>
													)}
												</TableCell>
												<TableCell>
													<Link
														target="_blank"
														href={`${PlatformRootUrl}/Emp/TermSheetForm/${sheet.dataRoomTermSheetRevisionGuid}?tab=facilities`}
													>
														View
													</Link>
												</TableCell>
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>
				</CerebroDrawer>
			)}
		</>
	);
};

function getFullTrancheDescription(item: TrancheEvaluation) {
	if (item.profileTangibleAssetType == 'New' || item.profileTangibleAssetType == 'Used') {
		return `${item.profileTangibleAssetType} ${EnumUtils.GetStructureTrancheDescription(item.structureTranche)}`;
	}

	if (item.profileRealEstateType && item.profileRealEstateType != 'None') {
		return `${EnumUtils.GetProfileRealEstateTypeDescription(
			item.profileRealEstateType
		)} ${EnumUtils.GetStructureTrancheDescription(item.structureTranche)}`;
	}

	return EnumUtils.GetStructureTrancheDescription(item.structureTranche);
}

interface LenderCountLinkProps {
	stat?: number;
	numMatches: number;
	tier1BankType: Tier1BankTypes | undefined;
	structureTranche: StructureTranches | undefined;

	finderFees?: boolean;

	loadLenders: (mode: number, tier1: number, finderFee: boolean, structure?: number | null) => void;
}

const LenderCountLink: React.FC<LenderCountLinkProps> = ({
	stat = 0,
	numMatches,
	tier1BankType,
	structureTranche,
	finderFees = false,
	loadLenders,
}) => {
	const title = finderFees
		? `of the ${stat} ${EnumUtils.GetTier1BankTypeDescription(tier1BankType)} finder fee deal size matches`
		: `of the ${stat} ${EnumUtils.GetTier1BankTypeDescription(tier1BankType)} size matches`;

	if (numMatches === 0)
		return (
			<CerebroText variant="small" component="span" title={title}>
				{numMatches}
			</CerebroText>
		);

	const onClick = () => {
		loadLenders(
			4,
			tier1BankType == 'Commercial' ? 10 : 20,
			finderFees,
			MatchingUtils.getStructureTrancheValue(structureTranche)
		);
	};

	return (
		<CerebroLink title={title} onClick={onClick} sx={{ cursor: 'pointer' }}>
			{numMatches}
		</CerebroLink>
	);
};

type LendersValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersValues>;
interface LendersValues {
	bankDealProfileGuid: string[];
}
